import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ConfigProvider, App as AntApp, theme as antdTheme } from 'antd';
import esES from 'antd/lib/locale/es_ES';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { store } from './store';
import { useAppSelector } from './hooks/redux';
import { useTheme } from './hooks/useTheme';
import { getPalette, FONT_FAMILY } from './theme/palette';
import { getDefaultRouteForRole, canAccessDashboard, hasCashierAccess, isAdmin, isManager, isSupportUser } from './utils/roleHelpers';
import { clearPlanIntent, savePlanIntent } from './utils/planIntent';
import { useGetBoolSettingQuery } from './services/settingsApi';

import AuthProvider from './components/auth/AuthProvider';
import SubscriptionSyncProvider from './components/auth/SubscriptionSyncProvider';
import PrintServicesSettingsSync from './components/auth/PrintServicesSettingsSync';
import AuthenticatedRedirect from './components/auth/AuthenticatedRedirect';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import MainLayout from './components/layout/MainLayout';
import PageTitle from './components/common/PageTitle';
import Dashboard from './pages/Dashboard';
import Tables from './pages/Tables';
import Orders from './pages/Orders';
import PaymentMethodsAdmin from './pages/PaymentMethodsAdmin';
import Reports from './pages/Reports';
import AdminAllCashRegistersView from './components/cashRegister/AdminAllCashRegistersView';
import CajaTabs from './components/cashRegister/CajaTabs';
import ShiftClosings from './pages/ShiftClosings';
// import ReceiptManagement from './components/receipts/ReceiptManagement'; // TODO: Implement backend endpoints
import Facturacion from './pages/Facturacion';
import ClientesFiscales from './pages/ClientesFiscales';
import MenuManagement from './pages/MenuManagement';
import UserManagement from './pages/UserManagement';
import RestaurantSettings from './pages/RestaurantSettings';
import SucursalesPage from './pages/SucursalesPage';
import AreasTablesAdmin from './pages/AreasTablesAdmin';
import SystemSettings from './pages/SystemSettings';
import FacturacionSettings from './pages/FacturacionSettings';
import ConfiguracionPage from './pages/ConfiguracionPage';
import PrinterSettings from './pages/PrinterSettings';
import AiSettings from './pages/AiSettings';
import MercadoPagoSettings from './pages/MercadoPagoSettings';
import TerminalesSettings from './pages/TerminalesSettings';
import GetnetSettings from './pages/GetnetSettings';
import Inventory from './pages/Inventory';
import Finanzas from './pages/Finanzas';
import PorRevisar from './pages/PorRevisar';
import SessionExpired from './pages/SessionExpired';
import AutofacturaPortal from './pages/AutofacturaPortal';
import PublicMenuPage from './pages/PublicMenuPage';
import KitchenDisplay from './pages/KitchenDisplay';
import DailyMenuAdmin from './pages/DailyMenuAdmin';
import Novedades from './pages/Novedades';
import OrderTaking from './pages/OrderTaking';
import SelectPlan from './pages/SelectPlan';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import SubscriptionManagement from './pages/SubscriptionManagement';

import './App.css';

dayjs.locale('es');

// reCAPTCHA site key - leave empty in .env to disable in development
// When empty, both frontend and backend will skip reCAPTCHA validation
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  // If authenticated, use AuthenticatedRedirect to check real subscription status
  // This queries Stripe directly to ensure we redirect based on actual subscription state
  if (isAuthenticated) {
    return <AuthenticatedRedirect />;
  }

  // Only use reCAPTCHA if a valid key is provided
  if (RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY !== '') {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
        <LoginForm onSwitchToRegister={() => navigate('/register')} />
      </GoogleReCaptchaProvider>
    );
  }

  // Render without reCAPTCHA if no key is provided
  return <LoginForm onSwitchToRegister={() => navigate('/register')} />;
};

// Guards the Cierre de Turno page. The feature is hidden by default and gated
// by the CierreTurnoActivo setting; only Admin/Gerente (or support) may see it.
// Backend endpoints enforce the same setting — this is the UX-level redirect so
// a direct URL doesn't land on a dead page. Assumes the caller already checked
// isAuthenticated.
const ShiftClosingsRoute: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { data: cierreTurnoSetting, isLoading } = useGetBoolSettingQuery('CierreTurnoActivo');
  const isEnabled = cierreTurnoSetting?.value ?? false;
  const hasAccess = isSupportUser(user?.email) || isAdmin(user?.role) || isManager(user?.role);

  // Wait for both the user and the setting before deciding, so we don't redirect
  // on a transient loading state.
  if (!user || isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Cargando...</div>
      </div>
    );
  }

  if (!isEnabled || !hasAccess) {
    return <Navigate to={getDefaultRouteForRole(user?.role)} replace />;
  }

  return (
    <MainLayout>
      <ShiftClosings />
    </MainLayout>
  );
};

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  // Check if there's a plan in the URL
  const searchParams = new URLSearchParams(window.location.search);
  const planFromUrl = searchParams.get('plan');

  // El plan que eligió en la landing: el registro abre una prueba sin tarjeta, así que
  // se recuerda para destacarlo cuando llegue a elegir plan (antes se perdía aquí).
  // Solo para quien se va a registrar: con sesión abierta, el ?plan= ya lo maneja
  // AuthenticatedRedirect. Y un /register sin plan borra lo que hubiera de antes, para
  // que otra alta en el mismo navegador no herede la elección de alguien más.
  React.useEffect(() => {
    if (isAuthenticated) return;
    if (planFromUrl) savePlanIntent(planFromUrl);
    else clearPlanIntent();
  }, [planFromUrl, isAuthenticated]);

  // If authenticated, use AuthenticatedRedirect to check real subscription status
  // Pass planFromUrl so it can redirect to select-plan with the plan pre-selected
  if (isAuthenticated) {
    return <AuthenticatedRedirect planFromUrl={planFromUrl} />;
  }

  // Only use reCAPTCHA if a valid key is provided
  if (RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY !== '') {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
        <RegisterForm onSwitchToLogin={() => navigate('/login')} />
      </GoogleReCaptchaProvider>
    );
  }

  // Render without reCAPTCHA if no key is provided
  return <RegisterForm onSwitchToLogin={() => navigate('/login')} />;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, user, loading, accessToken, refreshToken } = useAppSelector((state) => state.auth);
  const [isInitializing, setIsInitializing] = React.useState(true);

  // Wait a moment for AuthProvider to initialize and load user data
  React.useEffect(() => {
    const hasTokens = accessToken && refreshToken;

    // If we have tokens and user is loaded, we're ready
    if (user) {
      setIsInitializing(false);
      return;
    }

    // If we have tokens but no user yet, wait for AuthProvider to load the user
    if (hasTokens && !user) {
      // Set a timeout to avoid infinite waiting
      const timeout = setTimeout(() => {
        setIsInitializing(false);
      }, 2000); // Wait max 2 seconds

      return () => clearTimeout(timeout);
    }

    // No tokens, not logged in - proceed to show login
    setIsInitializing(false);
  }, [accessToken, refreshToken, user]);

  // Show loading while checking authentication or while loading from Redux
  if (loading || isInitializing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div>Cargando...</div>
        {isInitializing && <div style={{ fontSize: '12px', color: 'var(--color-neutral-text)' }}>Restaurando sesión...</div>}
      </div>
    );
  }

  return (
    <Router>
      <PageTitle />
      <Routes>
        {/* Public routes - outside AuthProvider */}
        <Route
          path="/login"
          element={<LoginPage />}
        />

        <Route
          path="/auth/login"
          element={<LoginPage />}
        />

        <Route
          path="/register"
          element={<RegisterPage />}
        />

        <Route
          path="/auth/register"
          element={<RegisterPage />}
        />

        <Route
          path="/session-expired"
          element={<SessionExpired />}
        />

        {/* Public autofactura portal */}
        <Route path="/autofactura" element={<AutofacturaPortal />} />
        <Route path="/autofactura/:code" element={<AutofacturaPortal />} />

        {/* Public digital menu (QR) */}
        <Route path="/menu/:slug" element={<PublicMenuPage />} />
        <Route path="/menu/:slug/hoy" element={<PublicMenuPage soloHoy />} />

        {/* Subscription routes - require auth but no MainLayout */}
        {/* Wrapped in SubscriptionSyncProvider to sync subscription status from Stripe */}
        <Route
          path="/select-plan"
          element={
            isAuthenticated ? (
              <SubscriptionSyncProvider>
                <SelectPlan />
              </SubscriptionSyncProvider>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/subscription/success"
          element={
            isAuthenticated ? (
              <SubscriptionSyncProvider>
                <SubscriptionSuccess />
              </SubscriptionSyncProvider>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/subscription/cancel"
          element={
            isAuthenticated ? (
              <SubscriptionSyncProvider>
                <SubscriptionCancel />
              </SubscriptionSyncProvider>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Protected routes - inside AuthProvider */}
        <Route path="/*" element={
          <AuthProvider>
            <PrintServicesSettingsSync>
            <Routes>

        <Route
          path="/dashboard"
          element={
            isAuthenticated ? (
              !user ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                  <div>Cargando...</div>
                </div>
              ) : canAccessDashboard(user?.role) ? (
                <MainLayout>
                  <Dashboard />
                </MainLayout>
              ) : (
                <Navigate to={getDefaultRouteForRole(user?.role)} replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        
        <Route
          path="/tables"
          element={
            isAuthenticated ? (
              <MainLayout>
                <Tables />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/orders"
          element={
            isAuthenticated ? (
              !user ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                  <div>Cargando...</div>
                </div>
              ) : hasCashierAccess(user?.role) ? (
                <MainLayout>
                  <Orders />
                </MainLayout>
              ) : (
                <Navigate to={getDefaultRouteForRole(user?.role)} replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* The old chooser page is gone: /new-order lands on takeout with the
            type switch inside the form. One route element for both types keeps
            the component mounted (and the cart intact) when switching. */}
        <Route path="/new-order" element={<Navigate to="/new-order/takeout" replace />} />

        <Route
          path="/new-order/:type"
          element={
            isAuthenticated ? (
              !user ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                  <div>Cargando...</div>
                </div>
              ) : hasCashierAccess(user?.role) ? (
                <MainLayout>
                  <OrderTaking />
                </MainLayout>
              ) : (
                <Navigate to={getDefaultRouteForRole(user?.role)} replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/payment-methods"
          element={
            isAuthenticated ? (
              <MainLayout>
                <PaymentMethodsAdmin />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/reports"
          element={
            isAuthenticated ? (
              <MainLayout>
                <Reports />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/cash-register"
          element={
            isAuthenticated ? (
              <MainLayout>
                <CajaTabs />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/admin/cash-registers"
          element={
            isAuthenticated ? (
              <MainLayout>
                <AdminAllCashRegistersView />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/shift-closings"
          element={
            isAuthenticated ? (
              <ShiftClosingsRoute />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/facturas"
          element={
            isAuthenticated ? (
              <MainLayout>
                <Facturacion />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/finanzas"
          element={
            isAuthenticated ? (
              <MainLayout>
                <Finanzas />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/clientes-fiscales"
          element={
            isAuthenticated ? (
              <MainLayout>
                <ClientesFiscales />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* TODO: Implement backend endpoints for Receipts module
        <Route
          path="/receipts"
          element={
            isAuthenticated ? (
              <MainLayout>
                <ReceiptManagement />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        */}

        <Route
          path="/menu-management"
          element={
            isAuthenticated ? (
              <MainLayout>
                <MenuManagement />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/user-management"
          element={
            isAuthenticated ? (
              <MainLayout>
                <UserManagement />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/restaurant-settings"
          element={
            isAuthenticated ? (
              <MainLayout>
                <RestaurantSettings />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/sucursales"
          element={
            isAuthenticated ? (
              <MainLayout>
                <SucursalesPage />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/areas-tables"
          element={
            isAuthenticated ? (
              <MainLayout>
                <AreasTablesAdmin />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/system-settings"
          element={
            isAuthenticated ? (
              <MainLayout>
                <SystemSettings />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/facturacion-settings"
          element={
            isAuthenticated ? (
              <MainLayout>
                <FacturacionSettings />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/por-revisar"
          element={
            isAuthenticated ? (
              <MainLayout>
                <PorRevisar />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/configuracion"
          element={
            isAuthenticated ? (
              <MainLayout>
                <ConfiguracionPage />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/printer-settings"
          element={
            isAuthenticated ? (
              <MainLayout>
                <PrinterSettings />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/ai-settings"
          element={
            isAuthenticated ? (
              <MainLayout>
                <AiSettings />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* El menú digital vive dentro de Gestión del Menú (su propia
            pestaña). La ruta vieja se conserva como redirección porque quedó
            publicada en la novedad que se les mandó. */}
        <Route path="/menu-digital-settings" element={<Navigate to="/menu-management" replace />} />

        <Route
          path="/terminal-settings"
          element={
            isAuthenticated ? (
              <MainLayout>
                <TerminalesSettings />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/mercadopago-settings"
          element={
            isAuthenticated ? (
              <MainLayout>
                <MercadoPagoSettings />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/getnet-settings"
          element={
            isAuthenticated ? (
              <MainLayout>
                <GetnetSettings />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/inventory"
          element={
            isAuthenticated ? (
              !user ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                  <div>Cargando...</div>
                </div>
              ) : canAccessDashboard(user?.role) ? (
                <MainLayout>
                  <Inventory />
                </MainLayout>
              ) : (
                <Navigate to={getDefaultRouteForRole(user?.role)} replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/daily-menus"
          element={
            isAuthenticated ? (
              <MainLayout>
                <DailyMenuAdmin />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/novedades"
          element={
            isAuthenticated ? (
              canAccessDashboard(user?.role) || isSupportUser(user?.email) ? (
                <MainLayout>
                  <Novedades />
                </MainLayout>
              ) : (
                <Navigate to={getDefaultRouteForRole(user?.role)} replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/kitchen"
          element={
            isAuthenticated ? (
              <MainLayout>
                <KitchenDisplay />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/subscription"
          element={
            isAuthenticated ? (
              !user ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                  <div>Cargando...</div>
                </div>
              ) : isAdmin(user?.role) || isSupportUser(user?.email) ? (
                <MainLayout>
                  <SubscriptionManagement />
                </MainLayout>
              ) : (
                <Navigate to={getDefaultRouteForRole(user?.role)} replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/system-settings"
          element={
            isAuthenticated ? (
              <MainLayout>
                <SystemSettings />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/"
          element={
            <Navigate
              to={isAuthenticated ? getDefaultRouteForRole(user?.role) : "/login"}
              replace
            />
          }
        />

        {/* Catch all route */}
        <Route
          path="*"
          element={
            <Navigate
              to={isAuthenticated ? getDefaultRouteForRole(user?.role) : "/login"}
              replace
            />
          }
        />
            </Routes>
            </PrintServicesSettingsSync>
          </AuthProvider>
        } />
      </Routes>
    </Router>
  );
};

// Themed wrapper component that reads theme from Redux store
const ThemedApp: React.FC = () => {
  const { resolvedTheme } = useAppSelector((state) => state.theme);

  // Initialize theme hook to set up system preference listener and apply data-theme attribute
  useTheme();

  const palette = getPalette(resolvedTheme === 'dark');

  return (
    <ConfigProvider
      locale={esES}
      theme={{
        algorithm: resolvedTheme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          // Piel "Marca". Los valores viven en theme/palette.ts, que es el
          // espejo verificado de los --color-* de index.css: sin eso los
          // componentes de AntD y el CSS propio derivan a marcas distintas.
          colorPrimary: palette.primary,
          colorSuccess: palette.success,
          colorError: palette.error,
          colorWarning: palette.warning,
          colorInfo: palette.info,
          colorBgLayout: palette.bgLayout,
          fontFamily: FONT_FAMILY,
          borderRadius: 8,
          // Mobile-friendly touch targets
          controlHeight: 44,
          controlHeightLG: 48,
          controlHeightSM: 40,
        },
        components: {
          Layout: {
            siderBg: resolvedTheme === 'dark' ? '#121b1a' : '#fff',
            headerBg: resolvedTheme === 'dark' ? '#121b1a' : '#fff',
          },
          Form: {
            itemMarginBottom: 4,
            verticalLabelPadding: '0 0 1px',
          },
          Divider: {
            marginLG: 8,
          },
          Table: {
            cellPaddingInline: 8,
            cellPaddingBlock: 8,
          },
          Button: {
            paddingInline: 16,
            paddingBlock: 8,
          },
          Modal: {
            borderRadius: 12,
          },
          Card: {
            borderRadius: 12,
            paddingLG: 16,
          },
        },
      }}
    >
      <AntApp>
        <AppContent />
      </AntApp>
    </ConfigProvider>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ThemedApp />
    </Provider>
  );
}

export default App;
